// Lib
import { useLayoutEffect, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import Cropper from 'cropperjs';

// Utils
import { fetchImageUsingCapacitorHTTP, loadImageIntoCanvas } from '../utils/imageModalImageBlobUtils';
import { loadCanvasIntoCropper } from '../utils/cropperUtils';

// Types
import type { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

// Logging
import globalLogger from '../../../../../logger';

const logger = globalLogger.createChannel('image.modal.editor');

export const IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY = {
    CLEAR_IMAGE_BEFORE_LOAD: 'clearImageBeforeLoad',
    KEEP_PREVIOUS_IMAGE_UNTIL_READY: 'keepPreviousImageUntilReady',
} as const;

type ImageModalEditorImageLoadStrategy =
    (typeof IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY)[keyof typeof IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY];

const destroyCropper = (cropperRef: MutableRefObject<Cropper | null>) => {
    if (!cropperRef.current) return;

    cropperRef.current.destroy();
    cropperRef.current = null;
};

const loadEditorImageDataUrl = async (sourceUrl: string, imageType: string): Promise<string> => {
    const { objectUrl } = await fetchImageUsingCapacitorHTTP(sourceUrl, imageType);

    try {
        const { dataUrl } = await loadImageIntoCanvas(objectUrl, imageType);
        return dataUrl;
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

/**
 * Loads the cropper source image using the requested v1/v2 image replacement strategy.
 */
export const useImageModalEditorImageLoader = ({
    imageRef,
    cropperRef,
    isDirtyRef,
    element,
    imageType,
    loadKey,
    loadStrategy,
    sourceUrl,
    onEditorReadyChange,
    onImageDataUrlLoad,
}: {
    imageRef: RefObject<HTMLImageElement | null>;
    cropperRef: MutableRefObject<Cropper | null>;
    isDirtyRef: MutableRefObject<boolean>;
    element: ImMNElement;
    imageType: string;
    loadKey: string;
    loadStrategy: ImageModalEditorImageLoadStrategy;
    sourceUrl?: string;
    onEditorReadyChange: (ready: boolean) => void;
    onImageDataUrlLoad?: (dataUrl: string) => void;
}) => {
    const elementRef = useRef(element);
    const loadGenerationRef = useRef(0);
    const shouldClearImageBeforeLoad = loadStrategy === IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY.CLEAR_IMAGE_BEFORE_LOAD;

    // Cropper's ready callback can run after React has received a newer element object.
    // Keep the ref current without restarting the image load for object-identity-only changes.
    elementRef.current = element;

    useLayoutEffect(() => () => destroyCropper(cropperRef), [cropperRef]);

    useLayoutEffect(() => {
        onEditorReadyChange(false);

        if (shouldClearImageBeforeLoad) {
            destroyCropper(cropperRef);
            imageRef.current?.removeAttribute('src');
        }

        if (!imageRef.current || !sourceUrl) return;

        let cancelled = false;
        const loadGeneration = loadGenerationRef.current + 1;
        loadGenerationRef.current = loadGeneration;

        // Fetching the image and initialising Cropper both finish asynchronously. If a newer
        // image load starts first, the older callbacks must not replace the image or mark the
        // editor ready.
        const canUseLoadedImage = () => !cancelled && loadGenerationRef.current === loadGeneration;

        const fetchAndLoadImage = async () => {
            try {
                // Use Capacitor HTTP so the editor can read image data without CORS failures.
                const dataUrl = await loadEditorImageDataUrl(sourceUrl, imageType);

                // A later load may have started while this image was being fetched.
                if (!canUseLoadedImage()) return;

                if (imageRef.current instanceof HTMLImageElement) {
                    destroyCropper(cropperRef);
                    imageRef.current.src = dataUrl;
                    onImageDataUrlLoad?.(dataUrl);

                    loadCanvasIntoCropper(
                        imageRef,
                        cropperRef,
                        elementRef.current,
                        onEditorReadyChange,
                        isDirtyRef,
                        canUseLoadedImage,
                    );
                }
            } catch (error) {
                // Sensitive data audit: SAFE — warn/error auto-sanitized (image file names/URLs redacted, error normalized)
                logger.error('Failed to load image for editing', error);
            }
        };

        fetchAndLoadImage();

        return () => {
            cancelled = true;
            if (shouldClearImageBeforeLoad) destroyCropper(cropperRef);
        };
    }, [
        cropperRef,
        imageRef,
        imageType,
        isDirtyRef,
        loadKey,
        onImageDataUrlLoad,
        onEditorReadyChange,
        shouldClearImageBeforeLoad,
        sourceUrl,
    ]);
};
